// client/src/components/pages/Settings/GroupingModal.tsx (converted from GroupingModal.js,
// ST-00226 — CLAUDE.md TypeScript rule). Create / edit a route grouping.
//
// ST-00226: onSave receives the saved grouping as the server returned it, so the page can
// show the warning a global admin gets when the grouping's organisation owns neither end
// of some pairs. A tenant user's save with such pairs is refused by the server (400,
// naming the pairs), and that message is shown here as any other save error.
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import type { IconType } from 'react-icons';
import api from '../../../services/api';
import {
  FaTimes,
  FaPlus,
  FaTruck,
  FaWarehouse,
  FaSearch,
  FaCheck,
  FaChevronDown,
  FaChevronUp,
  FaExclamationCircle,
  FaLongArrowAltRight,
} from 'react-icons/fa';
import ThemedSelect from '../../common/ThemedSelect';
import {
  DEFAULT_FALLBACK_METHOD,
  DEFAULT_MATCHING_METHOD,
  FALLBACK_OPTIONS,
  LOAD_MATCHING_FALLBACK_HELP,
  LOAD_MATCHING_OPTIONS,
  normaliseFallbackMethod,
  normaliseMatchingMethod,
} from './loadMatchingSetting';
import type { LegacyMethod, MatchingMethod } from '../../../types/routeFigures';
import type {
  GroupingApiError,
  RouteGrouping,
  RouteGroupingRule,
  SavedRouteGrouping,
} from '../../../types/clientPages/routeGroupings';

type Accent = 'dispatch' | 'receipt';
type RuleVerb = 'Exclude' | 'Include';
/** A rule while it is edited (the column may still be empty). */
type EditableRule = RouteGroupingRule;
interface FieldErrors {
  name?: string;
  dispatch?: string;
  receipt?: string;
}

// Columns available for exclusion / include-only rules (LexiBridgeDataView).
// 'Ticket Type' is deliberately absent: use a rule's Applies to setting to scope
// it to one side. A rule ON ticket_type removes the other side's tickets from the
// result set entirely, leaving dispatches with no receipts to reconcile against.
const RULE_COLUMNS: readonly string[] = [
  'Dispatch Organisation',
  'Dispatch Location',
  'Receipt Organisation',
  'Receipt Location',
  'Product',
  'Haulier',
  'Driver',
  'Reg No',
  'Trailer Reg 1',
  'Trailer Reg 2',
  'Product Ref.',
  'Ref No',
  'Order No',
  'Delivery No',
  'Revision No',
  'Security Seal 1',
  'Security Seal 2',
  'ImportSource',
  'AmendmentUser',
];

// Which ticket side a rule constrains. Tickets on the other side are exempt from
// the rule but stay in the result set, so a dispatch-side and a receipt-side rule
// can describe the same material in each side's own vocabulary.
const RULE_SIDES = [
  { value: 'both', label: 'Both sides' },
  { value: 'dispatch', label: 'Dispatch only' },
  { value: 'receipt', label: 'Receipt only' },
];

// Drop half-finished rules and send a consistent shape. Rules saved before side
// scoping existed have no side; 'both' is what they have always meant.
const toRulePayload = (rules: EditableRule[]): RouteGroupingRule[] =>
  rules
    .filter((r) => r.column && r.values.length > 0)
    .map((r) => ({ column: r.column, values: r.values, side: r.side || 'both' }));

const ACCENTS: Record<Accent, { Icon: IconType; tile: string; chip: string; chipRemove: string }> =
  {
    dispatch: {
      Icon: FaTruck,
      tile: 'bg-orange-100 text-orange-600',
      chip: 'bg-orange-50 border-orange-200 text-orange-900',
      chipRemove: 'text-orange-400 hover:text-orange-700',
    },
    receipt: {
      Icon: FaWarehouse,
      tile: 'bg-emerald-100 text-emerald-600',
      chip: 'bg-emerald-50 border-emerald-200 text-emerald-900',
      chipRemove: 'text-emerald-400 hover:text-emerald-700',
    },
  };

/**
 * Searchable multi-select for locations. Opens the full list on focus so users
 * can browse everything that's available (not just type-to-reveal), filters as
 * they type, and supports full keyboard navigation.
 */
interface LocationPickerProps {
  id: string;
  label: string;
  accent: Accent;
  locations: string[];
  selected: string[];
  onChange: (next: string[]) => void;
  loading: boolean;
  loadError: boolean;
  onRetry: () => void;
  fieldError?: string;
}

const LocationPicker = ({
  id,
  label,
  accent,
  locations,
  selected,
  onChange,
  loading,
  loadError,
  onRetry,
  fieldError,
}: LocationPickerProps) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);
  const { Icon, tile, chip, chipRemove } = ACCENTS[accent];

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return locations;
    return locations.filter((loc) => loc.toLowerCase().includes(q));
  }, [locations, query]);

  // Close when clicking outside the picker
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Keep the keyboard-active option scrolled into view
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector<HTMLElement>('[data-active="true"]');
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, open]);

  // Clamp active index when the filtered list changes
  useEffect(() => {
    setActiveIndex((i) => Math.min(i, Math.max(filtered.length - 1, 0)));
  }, [filtered.length]);

  const toggle = useCallback(
    (loc: string) => {
      if (selected.includes(loc)) {
        onChange(selected.filter((l) => l !== loc));
      } else {
        onChange([...selected, loc]);
      }
    },
    [selected, onChange]
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      // Never submit the form from the search box
      e.preventDefault();
      const active = filtered[activeIndex];
      if (open && active) {
        toggle(active);
      } else {
        setOpen(true);
      }
    } else if (e.key === 'Escape') {
      if (open) {
        // Close the list without closing the modal
        e.stopPropagation();
        setOpen(false);
      }
    } else if (e.key === 'Backspace' && query === '' && selected.length > 0) {
      onChange(selected.slice(0, -1));
    }
  };

  const listboxId = `${id}-listbox`;

  return (
    <div ref={containerRef}>
      <div className="flex items-center justify-between mb-1.5">
        <label htmlFor={id} className="flex items-center gap-2 text-sm font-medium text-slate-700">
          <span className={`inline-flex items-center justify-center w-6 h-6 rounded-md ${tile}`}>
            <Icon className="w-3 h-3" />
          </span>
          {label}
          <span className="text-red-500" aria-hidden="true">
            *
          </span>
        </label>
        {selected.length > 0 && (
          <span className="text-xs font-medium text-slate-500 tabular-nums">
            {selected.length} selected
          </span>
        )}
      </div>

      <div className="relative">
        <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
        <input
          type="text"
          id={id}
          role="combobox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={
            open && filtered[activeIndex] ? `${id}-opt-${activeIndex}` : undefined
          }
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            setActiveIndex(0);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder="Search or browse locations..."
          autoComplete="off"
          className={`w-full pl-9 pr-3 py-2 text-sm border rounded-md bg-surface text-slate-900 placeholder-slate-500 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/25 transition-colors ${
            fieldError ? 'border-red-300' : 'border-slate-300'
          }`}
        />
      </div>

      {/* In-flow listbox: avoids clipping inside the scrolling form and lets
          users pick several locations without the list closing */}
      {open && (
        <div className="mt-1 border border-slate-200 rounded-md bg-surface shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-3 space-y-2 animate-pulse" aria-hidden="true">
              <div className="h-4 bg-slate-100 rounded w-3/4"></div>
              <div className="h-4 bg-slate-100 rounded w-2/3"></div>
              <div className="h-4 bg-slate-100 rounded w-4/5"></div>
            </div>
          ) : loadError ? (
            <div className="p-3 text-sm text-slate-600 flex items-center justify-between gap-3">
              <span className="flex items-center gap-2 text-red-700">
                <FaExclamationCircle className="w-3.5 h-3.5 flex-shrink-0" />
                Couldn't load locations
              </span>
              <button
                type="button"
                onClick={onRetry}
                className="px-2.5 py-1 text-xs font-medium border border-slate-300 rounded-md text-slate-700 hover:bg-slate-50 transition-colors"
              >
                Retry
              </button>
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-3 text-sm text-slate-600">
              {locations.length === 0 ? (
                <>
                  <p className="font-medium text-slate-700">No locations available yet</p>
                  <p className="text-xs text-slate-500 mt-1">
                    Locations come from transaction data — a location appears here once tickets
                    exist for it.
                  </p>
                </>
              ) : (
                <>
                  <p>
                    No locations match <span className="font-medium text-slate-800">"{query}"</span>
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    Only locations with transaction data are listed. New locations appear after
                    their first tickets load.
                  </p>
                </>
              )}
            </div>
          ) : (
            <>
              <ul
                ref={listRef}
                id={listboxId}
                role="listbox"
                aria-multiselectable="true"
                aria-label={label}
                className="max-h-52 overflow-y-auto py-1"
              >
                {filtered.map((loc, i) => {
                  const isSelected = selected.includes(loc);
                  const isActive = i === activeIndex;
                  return (
                    <li
                      key={loc}
                      id={`${id}-opt-${i}`}
                      role="option"
                      aria-selected={isSelected}
                      data-active={isActive || undefined}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => toggle(loc)}
                      onMouseEnter={() => setActiveIndex(i)}
                      className={`flex items-center justify-between gap-3 px-3 py-1.5 text-sm cursor-pointer ${
                        isActive ? 'bg-slate-100' : ''
                      } ${isSelected ? 'text-slate-900 font-medium' : 'text-slate-700'}`}
                    >
                      <span className="truncate">{loc}</span>
                      {isSelected && <FaCheck className="w-3 h-3 text-primary-600 flex-shrink-0" />}
                    </li>
                  );
                })}
              </ul>
              <div className="px-3 py-1.5 border-t border-slate-100 bg-slate-50 text-[11px] text-slate-500 tabular-nums">
                {query
                  ? `${filtered.length} of ${locations.length} locations`
                  : `${locations.length} locations · from transaction data`}
              </div>
            </>
          )}
        </div>
      )}

      {/* Selected locations as removable chips */}
      <div className="mt-2 flex flex-wrap gap-1.5 min-h-[1.75rem]">
        {selected.length === 0 ? (
          <p className="text-xs text-slate-500 self-center">
            None selected yet — search or browse above
          </p>
        ) : (
          selected.map((loc) => (
            <span
              key={loc}
              className={`inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 text-xs font-medium border rounded-full ${chip}`}
            >
              {loc}
              <button
                type="button"
                onClick={() => onChange(selected.filter((l) => l !== loc))}
                aria-label={`Remove ${loc}`}
                className={`p-0.5 rounded-full transition-colors ${chipRemove}`}
              >
                <FaTimes className="w-2.5 h-2.5" />
              </button>
            </span>
          ))
        )}
      </div>

      {fieldError && (
        <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
          <FaExclamationCircle className="w-3 h-3 flex-shrink-0" />
          {fieldError}
        </p>
      )}
    </div>
  );
};

interface RuleRowProps {
  rule: EditableRule;
  index: number;
  verb: RuleVerb;
  onColumnChange: (column: string) => void;
  onSideChange: (side: string) => void;
  onAddValue: (value: string) => void;
  onRemoveValue: (valueIndex: number) => void;
  onRemove: () => void;
}

/** One exclusion / include-only rule: column select + side scope + value chips. */
const RuleRow = ({
  rule,
  index,
  verb,
  onColumnChange,
  onSideChange,
  onAddValue,
  onRemoveValue,
  onRemove,
}: RuleRowProps) => {
  const [draft, setDraft] = useState('');
  const [draftError, setDraftError] = useState('');

  const commitDraft = () => {
    const v = draft.trim();
    if (!v) return;
    // A value of only wildcards matches every row — for an exclusion that
    // would silently drop everything, so refuse it outright.
    if (/^\*+$/.test(v)) {
      setDraftError('A value of only * would match every row — add some text around it');
      return;
    }
    onAddValue(v);
    setDraft('');
    setDraftError('');
  };

  return (
    <div className="border border-slate-200 rounded-lg p-3 bg-slate-50">
      <div className="flex items-start gap-2">
        <div className="flex-1 grid grid-cols-1 sm:grid-cols-12 gap-2">
          <ThemedSelect
            value={rule.column}
            onChange={(e) => onColumnChange(String(e.target.value))}
            className="w-full text-sm sm:col-span-4"
            aria-label={`${verb} rule ${index + 1} column`}
          >
            <option value="">Select a column...</option>
            {/* Keep a retired column (e.g. Ticket Type) selectable on rules that
                already use it, so editing another field doesn't silently reset it. */}
            {!RULE_COLUMNS.includes(rule.column) && rule.column && (
              <option value={rule.column}>{rule.column} (retired)</option>
            )}
            {RULE_COLUMNS.map((column) => (
              <option key={column} value={column}>
                {column}
              </option>
            ))}
          </ThemedSelect>

          <ThemedSelect
            value={rule.side || 'both'}
            onChange={(e) => onSideChange(String(e.target.value))}
            disabled={!rule.column}
            className="w-full text-sm sm:col-span-3"
            aria-label={`${verb} rule ${index + 1} applies to`}
          >
            {RULE_SIDES.map((side) => (
              <option key={side.value} value={side.value}>
                {side.label}
              </option>
            ))}
          </ThemedSelect>

          <div className="flex sm:col-span-5">
            <input
              type="text"
              value={draft}
              onChange={(e) => {
                setDraft(e.target.value);
                if (draftError) setDraftError('');
              }}
              disabled={!rule.column}
              placeholder={
                rule.column ? `${verb} value, * = wildcard, press Enter` : 'Pick a column first'
              }
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commitDraft();
                }
              }}
              className="flex-1 min-w-0 px-3 py-2 text-sm border border-slate-300 rounded-l-md bg-surface text-slate-900 placeholder-slate-500 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/25 disabled:bg-slate-100 disabled:text-slate-400 transition-colors"
            />
            <button
              type="button"
              onClick={commitDraft}
              disabled={!rule.column || !draft.trim()}
              aria-label="Add value"
              className="px-3 border border-l-0 border-slate-300 rounded-r-md bg-surface text-slate-600 hover:bg-slate-100 disabled:text-slate-300 disabled:hover:bg-surface transition-colors"
            >
              <FaPlus className="w-3 h-3" />
            </button>
          </div>
        </div>

        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove ${verb.toLowerCase()} rule ${index + 1}`}
          className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
        >
          <FaTimes className="w-3.5 h-3.5" />
        </button>
      </div>

      {draftError && (
        <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
          <FaExclamationCircle className="w-3 h-3 flex-shrink-0" />
          {draftError}
        </p>
      )}

      {rule.values.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {rule.values.map((value, valueIndex) => (
            <span
              key={valueIndex}
              title={
                value.includes('*')
                  ? 'Wildcard pattern (* matches anything, case-insensitive)'
                  : undefined
              }
              className={`inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 text-xs font-medium rounded-full ${
                value.includes('*')
                  ? 'bg-violet-50 border border-violet-300 text-violet-800 font-mono'
                  : 'bg-surface border border-slate-300 text-slate-700'
              }`}
            >
              {value}
              <button
                type="button"
                onClick={() => onRemoveValue(valueIndex)}
                aria-label={`Remove value ${value}`}
                className="p-0.5 rounded-full text-slate-400 hover:text-red-600 transition-colors"
              >
                <FaTimes className="w-2.5 h-2.5" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

interface RuleListProps {
  title: string;
  hint: string;
  verb: RuleVerb;
  rules: EditableRule[];
  setRules: (rules: EditableRule[]) => void;
  addLabel: string;
}

/** Exclusions or include-only rules list, sharing one visual language. */
const RuleList = ({ title, hint, verb, rules, setRules, addLabel }: RuleListProps) => {
  const updateRule = (index: number, patch: Partial<EditableRule>) => {
    setRules(rules.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-medium text-slate-700">{title}</h4>
          <p className="text-xs text-slate-500 mt-0.5">{hint}</p>
        </div>
        <button
          type="button"
          onClick={() => setRules([...rules, { column: '', values: [], side: 'both' }])}
          className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium border border-slate-300 rounded-md text-slate-700 bg-surface hover:bg-slate-50 transition-colors flex-shrink-0"
        >
          <FaPlus className="w-2.5 h-2.5" />
          {addLabel}
        </button>
      </div>

      {rules.length > 0 && (
        <div className="mt-2 space-y-2">
          {rules.map((rule, index) => (
            <RuleRow
              key={index}
              rule={rule}
              index={index}
              verb={verb}
              onColumnChange={(column) => updateRule(index, { column, values: [] })}
              onSideChange={(side) => updateRule(index, { side })}
              onAddValue={(value) => {
                const v = value.trim();
                if (v && !rule.values.includes(v)) {
                  updateRule(index, { values: [...rule.values, v] });
                }
              }}
              onRemoveValue={(valueIndex) =>
                updateRule(index, { values: rule.values.filter((_, i) => i !== valueIndex) })
              }
              onRemove={() => setRules(rules.filter((_, i) => i !== index))}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export interface GroupingModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called after a save with the grouping the server returned (with its `warning`, if any). */
  onSave: (saved?: SavedRouteGrouping) => void;
  /** The grouping being edited; null/undefined creates a new one. */
  grouping?: RouteGrouping | null;
}

const GroupingModal = ({ isOpen, onClose, onSave, grouping }: GroupingModalProps) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedDispatchLocations, setSelectedDispatchLocations] = useState<string[]>([]);
  const [selectedReceiptLocations, setSelectedReceiptLocations] = useState<string[]>([]);
  const [dispatchLocationsList, setDispatchLocationsList] = useState<string[]>([]);
  const [receiptLocationsList, setReceiptLocationsList] = useState<string[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [locationsError, setLocationsError] = useState(false);
  const [exclusions, setExclusions] = useState<EditableRule[]>([]);
  const [inclusions, setInclusions] = useState<EditableRule[]>([]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [includeInReporting, setIncludeInReporting] = useState(true);
  // ST-00218 P3: the route's "Load matching" (stored as bfcfMatchMode; Truck + time by default).
  const [bfcfMatchMode, setBfcfMatchMode] = useState<MatchingMethod>(DEFAULT_MATCHING_METHOD);
  // ST-00298: what Truck + time shows while load matching is unavailable (bfcfFallbackMode).
  const [bfcfFallbackMode, setBfcfFallbackMode] = useState<LegacyMethod>(DEFAULT_FALLBACK_METHOD);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const nameInputRef = useRef<HTMLInputElement | null>(null);
  const formRef = useRef<HTMLFormElement | null>(null);
  const initialSnapshotRef = useRef('');

  const serialize = useCallback(
    () =>
      JSON.stringify({
        name,
        description,
        selectedDispatchLocations,
        selectedReceiptLocations,
        exclusions,
        inclusions,
        includeInReporting,
        bfcfMatchMode,
        bfcfFallbackMode,
      }),
    [
      name,
      description,
      selectedDispatchLocations,
      selectedReceiptLocations,
      exclusions,
      inclusions,
      includeInReporting,
      bfcfMatchMode,
      bfcfFallbackMode,
    ]
  );

  // Initialize form when the target grouping changes
  useEffect(() => {
    const init = {
      name: grouping ? grouping.name : '',
      description: grouping ? grouping.description || '' : '',
      dispatch: grouping ? grouping.dispatchLocations : ([] as string[]),
      receipt: grouping ? grouping.receiptLocations : ([] as string[]),
      exclusions: grouping ? grouping.exclusions || [] : ([] as EditableRule[]),
      inclusions: grouping ? grouping.inclusions || [] : ([] as EditableRule[]),
      includeInReporting: grouping
        ? grouping.includeInReporting !== undefined
          ? grouping.includeInReporting
          : true
        : true,
      bfcfMatchMode: grouping
        ? normaliseMatchingMethod(grouping.bfcfMatchMode)
        : DEFAULT_MATCHING_METHOD,
      bfcfFallbackMode: grouping
        ? normaliseFallbackMethod(grouping.bfcfFallbackMode)
        : DEFAULT_FALLBACK_METHOD,
    };

    setName(init.name);
    setDescription(init.description);
    setSelectedDispatchLocations(init.dispatch);
    setSelectedReceiptLocations(init.receipt);
    setExclusions(init.exclusions);
    setInclusions(init.inclusions);
    setIncludeInReporting(init.includeInReporting);
    setBfcfMatchMode(init.bfcfMatchMode);
    setBfcfFallbackMode(init.bfcfFallbackMode);
    setShowAdvanced(init.exclusions.length > 0 || init.inclusions.length > 0);
    setFieldErrors({});
    setError(null);

    initialSnapshotRef.current = JSON.stringify({
      name: init.name,
      description: init.description,
      selectedDispatchLocations: init.dispatch,
      selectedReceiptLocations: init.receipt,
      exclusions: init.exclusions,
      inclusions: init.inclusions,
      includeInReporting: init.includeInReporting,
      bfcfMatchMode: init.bfcfMatchMode,
      bfcfFallbackMode: init.bfcfFallbackMode,
    });
  }, [grouping, isOpen]);

  const fetchLocations = useCallback(async () => {
    setLocationsLoading(true);
    setLocationsError(false);
    try {
      const [dispatchRes, receiptRes] = await Promise.all([
        api.data.getDispatchLocations(),
        api.data.getReceiptLocations(),
      ]);
      setDispatchLocationsList((dispatchRes.data as string[] | null) || []);
      setReceiptLocationsList((receiptRes.data as string[] | null) || []);
    } catch (err) {
      console.error('Error fetching locations:', err);
      setLocationsError(true);
    } finally {
      setLocationsLoading(false);
    }
  }, []);

  // Fetch locations and focus the name field when the modal opens
  useEffect(() => {
    if (!isOpen) return undefined;
    fetchLocations();
    const t = setTimeout(() => nameInputRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, [isOpen, fetchLocations]);

  const requestClose = useCallback(() => {
    if (serialize() !== initialSnapshotRef.current) {
      if (!window.confirm('Discard unsaved changes to this grouping?')) return;
    }
    onClose();
  }, [serialize, onClose]);

  // Escape closes the modal (pickers stop propagation when their list is open)
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') requestClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, requestClose]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const errors: FieldErrors = {};
    if (!name.trim()) errors.name = 'Give this grouping a name';
    if (selectedDispatchLocations.length === 0)
      errors.dispatch = 'Select at least one dispatch location';
    if (selectedReceiptLocations.length === 0)
      errors.receipt = 'Select at least one receipt location';

    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setError(null);
      if (errors.name) nameInputRef.current?.focus();
      formRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = {
        name: name.trim(),
        description: description.trim(),
        dispatchLocations: selectedDispatchLocations,
        receiptLocations: selectedReceiptLocations,
        exclusions: toRulePayload(exclusions),
        inclusions: toRulePayload(inclusions),
        includeInReporting,
        bfcfMatchMode,
        // ST-00298: only a Truck + time route sends its fallback; any other keeps the stored one
        // (the server sets it from the old mode when a route moves onto Truck + time)
        ...(bfcfMatchMode === 'engine' ? { bfcfFallbackMode } : {}),
      };

      const res = grouping
        ? await api.routeSummary.updateGrouping(grouping.id, data)
        : await api.routeSummary.createGrouping(data);

      onSave((res?.data as SavedRouteGrouping | undefined) ?? undefined);
    } catch (err) {
      console.error('Error saving grouping:', err);
      const e = err as GroupingApiError;
      setError(e.response?.data?.error || e.response?.data?.message || 'Failed to save grouping');
      formRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const advancedRuleCount =
    exclusions.filter((r) => r.column && r.values.length > 0).length +
    inclusions.filter((r) => r.column && r.values.length > 0).length;

  return (
    <div
      className="fixed inset-0 bg-slate-900/50 dark:bg-black/50 flex items-center justify-center z-50 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) requestClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="grouping-modal-title"
        className="bg-surface rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col animate-fadeIn motion-reduce:animate-none"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 id="grouping-modal-title" className="text-lg font-bold text-slate-900">
              {grouping ? 'Edit Grouping' : 'Create New Grouping'}
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Group dispatch and receipt locations into a route for summaries and reporting
            </p>
          </div>
          <button
            onClick={requestClose}
            aria-label="Close"
            className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <FaTimes className="w-4 h-4" />
          </button>
        </div>

        {/* Form */}
        <form
          id="grouping-form"
          ref={formRef}
          onSubmit={handleSubmit}
          className="p-6 overflow-y-auto flex-1 space-y-6"
        >
          {/* Server error */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2.5">
              <FaExclamationCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700 font-medium">{error}</p>
            </div>
          )}

          {/* Details */}
          <section>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label
                  htmlFor="groupingName"
                  className="block text-sm font-medium text-slate-700 mb-1.5"
                >
                  Grouping Name{' '}
                  <span className="text-red-500" aria-hidden="true">
                    *
                  </span>
                </label>
                <input
                  type="text"
                  id="groupingName"
                  ref={nameInputRef}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className={`w-full px-3 py-2 text-sm border rounded-md bg-surface text-slate-900 placeholder-slate-500 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/25 transition-colors ${
                    fieldErrors.name ? 'border-red-300' : 'border-slate-300'
                  }`}
                  placeholder="e.g. Witbank → Richards Bay"
                />
                {fieldErrors.name && (
                  <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
                    <FaExclamationCircle className="w-3 h-3 flex-shrink-0" />
                    {fieldErrors.name}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="groupingDescription"
                  className="block text-sm font-medium text-slate-700 mb-1.5"
                >
                  Description <span className="text-slate-400 font-normal">(optional)</span>
                </label>
                <textarea
                  id="groupingDescription"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md bg-surface text-slate-900 placeholder-slate-500 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/25 resize-none transition-colors"
                  placeholder="e.g. Coal deliveries from Witbank mines to Richards Bay terminal"
                />
              </div>
            </div>
          </section>

          {/* Route locations */}
          <section className="border border-slate-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-slate-900">Route Locations</h3>
            <p className="text-xs text-slate-500 mt-0.5 mb-4">
              Transactions dispatched from any location on the left and received at any location on
              the right belong to this grouping
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
              <LocationPicker
                id="dispatch-locations"
                label="Dispatch Locations"
                accent="dispatch"
                locations={dispatchLocationsList}
                selected={selectedDispatchLocations}
                onChange={(next) => {
                  setSelectedDispatchLocations(next);
                  if (next.length > 0) setFieldErrors((fe) => ({ ...fe, dispatch: undefined }));
                }}
                loading={locationsLoading}
                loadError={locationsError}
                onRetry={fetchLocations}
                fieldError={fieldErrors.dispatch}
              />

              <LocationPicker
                id="receipt-locations"
                label="Receipt Locations"
                accent="receipt"
                locations={receiptLocationsList}
                selected={selectedReceiptLocations}
                onChange={(next) => {
                  setSelectedReceiptLocations(next);
                  if (next.length > 0) setFieldErrors((fe) => ({ ...fe, receipt: undefined }));
                }}
                loading={locationsLoading}
                loadError={locationsError}
                onRetry={fetchLocations}
                fieldError={fieldErrors.receipt}
              />
            </div>
          </section>

          {/* Advanced filters (collapsed by default) */}
          <section className="border border-slate-200 rounded-lg overflow-hidden">
            <button
              type="button"
              onClick={() => setShowAdvanced((v) => !v)}
              aria-expanded={showAdvanced}
              className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-slate-50 transition-colors"
            >
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-slate-900">Refine Results</h3>
                <span className="text-xs text-slate-500">optional</span>
                {advancedRuleCount > 0 && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-50 text-primary-800 tabular-nums">
                    {advancedRuleCount} {advancedRuleCount === 1 ? 'rule' : 'rules'}
                  </span>
                )}
              </div>
              {showAdvanced ? (
                <FaChevronUp className="w-3 h-3 text-slate-400" />
              ) : (
                <FaChevronDown className="w-3 h-3 text-slate-400" />
              )}
            </button>

            {showAdvanced && (
              <div className="px-4 pb-4 pt-1 space-y-5 border-t border-slate-100">
                <p className="text-xs text-slate-500 pt-2">
                  Values match case-insensitively. Use{' '}
                  <code className="font-mono bg-slate-100 border border-slate-200 rounded px-1">
                    *
                  </code>{' '}
                  as a wildcard — e.g.{' '}
                  <code className="font-mono bg-slate-100 border border-slate-200 rounded px-1">
                    KPS-*
                  </code>{' '}
                  matches anything starting with "KPS-".
                </p>
                <p className="text-xs text-slate-500">
                  <span className="font-medium text-slate-600">Applies to</span> scopes a rule to
                  one side of the route. Tickets on the other side are left alone by that rule
                  rather than dropped, so when dispatch and receipt name the same product
                  differently you can add one rule per side and keep both tickets.
                </p>
                <RuleList
                  title="Exclusions"
                  hint="Drop rows where a column matches any of these values"
                  verb="Exclude"
                  rules={exclusions}
                  setRules={setExclusions}
                  addLabel="Add exclusion"
                />
                <RuleList
                  title="Include Only"
                  hint="Keep only rows where a column matches one of these values"
                  verb="Include"
                  rules={inclusions}
                  setRules={setInclusions}
                  addLabel="Add filter"
                />
              </div>
            )}
          </section>

          {/* Options */}
          <section className="border border-slate-200 rounded-lg p-4 space-y-4">
            <h3 className="text-sm font-semibold text-slate-900">Options</h3>

            <div>
              <label
                htmlFor="bfcfMatchMode"
                className="block text-sm font-medium text-slate-700 mb-1"
              >
                Load matching
              </label>
              <p className="text-xs text-slate-500 mb-2">
                How a receipt is paired with the dispatch it came from, for Brought Forward /
                Carried Forward and the per-day variance
              </p>
              <ThemedSelect
                id="bfcfMatchMode"
                value={bfcfMatchMode}
                onChange={(e) => {
                  const next = normaliseMatchingMethod(e.target.value);
                  // ST-00298: a route moved onto Truck + time keeps its old mode as the fallback
                  if (next === 'engine' && bfcfMatchMode !== 'engine') {
                    setBfcfFallbackMode(bfcfMatchMode);
                  }
                  setBfcfMatchMode(next);
                }}
                className="w-full text-sm"
                aria-describedby="bfcfMatchMode-help"
              >
                {LOAD_MATCHING_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </ThemedSelect>
              <div id="bfcfMatchMode-help" className="mt-2 space-y-1">
                <p className="text-xs text-slate-600">
                  {LOAD_MATCHING_OPTIONS.find((o) => o.value === bfcfMatchMode)?.help}
                </p>
                {bfcfMatchMode === 'engine' && (
                  <p className="text-xs text-slate-500">{LOAD_MATCHING_FALLBACK_HELP}</p>
                )}
              </div>
              {bfcfMatchMode === 'engine' && (
                <div className="mt-3">
                  <label
                    htmlFor="bfcfFallbackMode"
                    className="block text-sm font-medium text-slate-700 mb-1"
                  >
                    If load matching is unavailable, use
                  </label>
                  <ThemedSelect
                    id="bfcfFallbackMode"
                    value={bfcfFallbackMode}
                    onChange={(e) => setBfcfFallbackMode(normaliseFallbackMethod(e.target.value))}
                    className="w-full text-sm"
                    aria-describedby="bfcfFallbackMode-help"
                  >
                    {FALLBACK_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </ThemedSelect>
                  <p id="bfcfFallbackMode-help" className="mt-2 text-xs text-slate-600">
                    {FALLBACK_OPTIONS.find((o) => o.value === bfcfFallbackMode)?.help}
                  </p>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between gap-4 pt-4 border-t border-slate-100">
              <div className="flex-1">
                <label
                  htmlFor="includeInReporting"
                  className="block text-sm font-medium text-slate-700"
                >
                  Include in Reporting Sections
                </label>
                <p className="text-xs text-slate-500 mt-0.5">
                  Show this grouping in the Dashboard and other reporting sections
                </p>
              </div>
              <button
                type="button"
                id="includeInReporting"
                role="switch"
                aria-checked={includeInReporting}
                onClick={() => setIncludeInReporting(!includeInReporting)}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                  includeInReporting ? 'bg-primary-500' : 'bg-slate-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-surface shadow-sm transition-transform ${
                    includeInReporting ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </section>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between gap-3 flex-shrink-0">
          <p className="text-xs text-slate-500 tabular-nums flex items-center gap-1.5 min-w-0">
            <span className="font-medium text-slate-700">{selectedDispatchLocations.length}</span>
            dispatch
            <FaLongArrowAltRight className="w-3 h-3 text-slate-400 flex-shrink-0" />
            <span className="font-medium text-slate-700">{selectedReceiptLocations.length}</span>
            receipt
          </p>
          <div className="flex items-center gap-3 flex-shrink-0">
            <button
              type="button"
              onClick={requestClose}
              className="px-4 py-2 text-sm font-medium border border-slate-300 rounded-md text-slate-700 bg-surface hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="grouping-form"
              disabled={loading}
              className={`px-4 py-2 text-sm font-medium rounded-md text-white transition-colors flex items-center gap-2 ${
                loading ? 'bg-slate-400 cursor-not-allowed' : 'bg-primary-500 hover:bg-primary-600'
              }`}
            >
              {loading && (
                <svg
                  className="animate-spin h-3.5 w-3.5"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              )}
              {loading ? 'Saving...' : grouping ? 'Update Grouping' : 'Create Grouping'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupingModal;
